import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

function FAQItem({ question, answer, isOpen, onToggle }: FAQItemProps) {
  return (
    <div className="border-b border-gray-200">
      <button
        onClick={onToggle}
        className="w-full py-4 px-6 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-800 pr-4">{question}</span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="text-4xl text-primary-orange flex-shrink-0"
        >
          ▼
        </motion.span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-4 text-gray-600 whitespace-pre-line">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface FAQProps {
  onBack: () => void;
}

export function FAQ({ onBack }: FAQProps) {
  const { language } = useLanguage();

  // Redirect to comprehensive FAQ page - DISABLED to fix crash
  // useEffect(() => {
  //   window.location.href = '/early-childhood-faq.html';
  // }, []);

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqData = {
    en: {
      title: 'Frequently Asked Questions',
      subtitle: 'Everything you need to know about Wonderpath EcoGames',
      sections: [
        {
          title: 'About Early Childhood Edition (Ages 3-6)',
          items: [
            {
              question: 'What activities are included in the Early Childhood Edition?',
              answer: 'We offer multiple activities designed for young learners:\n\n🐰 "Help Benny the Bunny Get Home!" - An interactive hopping game that introduces young children to nature through play.\n\nBenny\'s Journey teaches:\n• Basic animal movements and habitats\n• Simple cause and effect (tap to hop)\n• Spatial awareness and direction\n• Celebration of achievement\n\n🎨 "EchoDraw" - A magical sound-to-color experience where sounds become visual art!\n\nEchoDraw offers:\n• No drawing required - just use your voice!\n• Make sounds, watch colors and shapes appear\n• Perfect for kids who prefer sound play over screen drawing\n• All ages welcome to explore\n• Playback feature - hear your creation sing back to you\n\nMore activities coming soon focused on:\n• Seasons and weather patterns\n• Colors and shapes in nature\n• Simple ecosystem concepts'
            },
            {
              question: 'What is EchoDraw? My child doesn\'t like drawing on screens.',
              answer: 'EchoDraw is perfect for kids who don\'t enjoy screen drawing! It\'s a playful sound-based activity where children use their VOICE instead of drawing tools.\n\nHow it works:\n1. Make sounds - hums, whistles, claps, singing, or any vocal sounds\n2. Watch them transform into colorful shapes and patterns\n3. Each sound creates different colors and visual textures\n4. Play back your creation - it sings your sounds back to you!\n\nWhy kids love it:\n• No drawing skills needed\n• Natural and intuitive - just make sounds!\n• Immediate visual feedback\n• Encourages vocal exploration and creativity\n• Great for kids who are more auditory or kinesthetic learners\n• All ages can explore and experiment\n\nEchoDraw is especially wonderful for:\n• Children who resist traditional screen activities\n• Kids who love music and sound\n• Exploring cause and effect (sound → color)\n• Sensory play and creative expression'
            },
            {
              question: 'Is the Early Childhood Edition safe for young children?',
              answer: 'Yes! The Early Childhood Edition is designed with safety and developmentally appropriate content for ages 3-6. Features include:\n\n• No personal information required\n• Ad-free experience\n• Age-appropriate visuals and interactions\n• Large, touch-friendly buttons\n• Positive reinforcement and encouragement\n• No external links (except parent-facing content)\n• Privacy-first design'
            },
            {
              question: 'How long should my child play?',
              answer: 'Activity times vary:\n• Bunny activity: 2-5 minutes per session\n• EchoDraw: 3-10 minutes of sound exploration\n\nFor young children ages 3-6, we recommend:\n• Short play sessions (5-10 minutes)\n• Supervised playtime\n• Taking breaks between activities\n• Using the games as one part of varied play experiences\n• Following your child\'s interest and energy level\n\nYou can replay as many times as your child enjoys! Some children love repeating EchoDraw to explore different sounds and patterns.'
            }
          ]
        },
        {
          title: 'About Wonderpath Eco Game (Ages 8-12+)',
          items: [
            {
              question: 'What is Wonderpath Eco Game?',
              answer: 'Wonderpath is a creative multiplayer drawing game where kids learn about ecosystems, climate science, and nature\'s regeneration through play and develop their innovative thinking and visual communication skills. Think Pictionary, but with arts and design support and focused on ecological concepts like pollination, food webs, and the water cycle.'
            },
            {
              question: 'Who is it for?',
              answer: 'The game is designed for ages 8-12+ (grades 3-8), but works great for families, classrooms, after-school programs, and anyone interested in learning about nature in a fun way.'
            },
            {
              question: 'How long does a session take?',
              answer: 'A typical game session takes 15-30 minutes, though you can play for as long or short as you like.'
            },
            {
              question: 'How many people can play?',
              answer: '1-8+ players can join a session. It works well solo, in small groups, or with whole classrooms.'
            }
          ]
        },
        {
          title: 'Getting Started',
          items: [
            {
              question: 'What do I need to play?',
              answer: 'Just an internet connection and a web browser on any device (computer, tablet, or phone). For the drawing game, you\'ll need a way to draw—mouse, trackpad, or touchscreen.'
            },
            {
              question: 'Is there an app to download?',
              answer: 'No download needed! Wonderpath is web-based and works right in your browser.\n\nNote: The game is currently in beta, and we\'re eager to get your feedback to improve it!'
            },
            {
              question: 'Do I need to create an account?',
              answer: 'No personal information or accounts required. Just choose a nickname and start playing!'
            }
          ]
        },
        {
          title: 'How to Play (Drawing Game)',
          items: [
            {
              question: 'How does the game work?',
              answer: 'Players take turns drawing ecological concepts while others try to guess what\'s being drawn. Each card teaches something about how nature works—from coral reefs to photosynthesis.'
            },
            {
              question: 'What are the challenge cards?',
              answer: 'There are 25 educational challenge cards organized into 5 categories:\n\n🌳 Living Things (forests, coral reefs, food webs)\n🌊 Natural Forces (water cycle, weather, soil, currents)\n🦋 Partnerships (pollination, symbiosis, seed dispersers)\n⚡ Nature\'s Powers (photosynthesis, adaptation, migration)\n⚖️ Balance & Solutions (habitat connectivity, ecosystem health)'
            },
            {
              question: 'Are there different difficulty levels?',
              answer: 'Yes! Each card has three difficulty levels (easy, medium, hard) to match different ages and learning levels.'
            },
            {
              question: 'What if I don\'t know how to draw something?',
              answer: 'That\'s part of the learning! You don\'t need to be a good artist—simple drawings work great. The game is about exploring and learning together.'
            }
          ]
        },
        {
          title: 'Learning & Education',
          items: [
            {
              question: 'What will kids learn?',
              answer: 'Players learn how ecosystems work, how living and non-living parts of nature interact, climate science basics, biodiversity concepts, and nature-based solutions to environmental challenges.'
            },
            {
              question: 'Is it aligned with educational standards?',
              answer: 'Yes! Wonderpath supports UN Sustainable Development Goals 4 (Quality Education), 13 (Climate Action), and 15 (Life on Land), and aligns with ecosystem, biodiversity, and climate science curriculum for grades 3-8.'
            },
            {
              question: 'Can teachers use this in classrooms?',
              answer: 'Absolutely! Teachers love it because there\'s no prep needed, it\'s curriculum-aligned, and it works for quick activities or longer sessions. It\'s great for introducing ecology units, team-building, or reviewing concepts.'
            },
            {
              question: 'Is the content scientifically accurate?',
              answer: 'Yes. All content is research-informed and based on established climate education frameworks from organizations like NAAEE (North American Association for Environmental Education) and UNESCO.'
            }
          ]
        },
        {
          title: 'Privacy & Safety',
          items: [
            {
              question: 'Is my child\'s information safe?',
              answer: 'Yes! Wonderpath is designed with privacy-first principles. We don\'t collect or store personal data. Players only use nicknames—no names, emails, or personal information required.'
            },
            {
              question: 'Is the game moderated?',
              answer: 'Yes, gameplay takes place in a closed, moderated system with age-appropriate content in a safe, ad-free environment.'
            },
            {
              question: 'What happens to drawings after a session?',
              answer: 'No data is stored after sessions end. Everything stays private to your game.'
            }
          ]
        },
        {
          title: 'Technical Support',
          items: [
            {
              question: 'What devices work with Wonderpath?',
              answer: 'Any device with a modern web browser works: computers, tablets, and smartphones.'
            },
            {
              question: 'What browsers are supported?',
              answer: 'Chrome, Firefox, Safari, Edge, and other modern browsers all work great.'
            },
            {
              question: 'The drawing isn\'t working smoothly. What should I do?',
              answer: 'Try using a different browser or device. If you\'re on a tablet or phone, make sure you\'re using your finger or a stylus directly on the screen.'
            },
            {
              question: 'Can I adjust drawing settings?',
              answer: 'Yes! You can adjust pen thickness and choose from different colors. There are also undo and erase functions to help with drawing.'
            }
          ]
        },
        {
          title: 'About Our Mission',
          items: [
            {
              question: 'What makes Wonderpath different?',
              answer: 'We focus on hope-centered climate education. Instead of emphasizing fear or doom, we teach about nature\'s incredible ability to regenerate and adapt, and inspire kids to see themselves as part of the solution.'
            },
            {
              question: 'Why focus on ecosystems?',
              answer: 'Understanding how ecosystems work is fundamental to climate literacy. When kids see how everything in nature is connected, they develop systems thinking skills that help them understand and address environmental challenges.'
            },
            {
              question: 'How does this support climate action?',
              answer: 'Education is one of the most powerful tools for climate action. By building ecological literacy in young people, we\'re preparing the next generation to make informed decisions and take meaningful action for our planet.'
            }
          ]
        },
        {
          title: 'For Educators',
          items: [
            {
              question: 'Where can I find teaching resources?',
              answer: 'Contact us at info@wonderpath.nyc for educator guides, lesson plans, and curriculum integration ideas.'
            },
            {
              question: 'Can Wonderpath be used for virtual learning?',
              answer: 'Yes! The game works great for both in-person and remote learning environments.'
            },
            {
              question: 'How can I use this in my classroom?',
              answer: 'Use it to introduce or review ecology units, as a formative assessment tool, for team-building activities, or as an engaging way to discuss environmental concepts. It works for quick 15-minute activities or longer 30+ minute sessions.'
            },
            {
              question: 'Are there assessment tools available?',
              answer: 'The game naturally facilitates discussion and reveals student understanding. Contact us at info@wonderpath.nyc to discuss assessment tools and learning outcome tracking.'
            }
          ]
        },
        {
          title: 'Contact & Support',
          items: [
            {
              question: 'How can I get in touch?',
              answer: 'Email: info@wonderpath.nyc\nWebsite: https://eco-games-beta.vercel.app/\n\nThe game is currently in beta—we\'d love your feedback to make it even better!'
            },
            {
              question: 'Where can I share feedback or suggestions?',
              answer: 'We\'d love to hear from you! Email us at info@wonderpath.nyc with your thoughts, ideas, questions, or any bugs you encounter. Your feedback helps us improve the game for everyone.'
            },
            {
              question: 'Can my organization partner with Wonderpath?',
              answer: 'Yes! We\'re actively seeking partnerships with schools, environmental organizations, and educational foundations. Email us at info@wonderpath.nyc to explore collaboration opportunities.'
            }
          ]
        }
      ],
      tagline: '"Where creativity meets climate education."',
      mission: 'Wonderpath Eco Game • Supporting UN Goals for Quality Education, Climate Action & Life on Land'
    },
    es: {
      title: 'Preguntas Frecuentes',
      subtitle: 'Todo lo que necesitas saber sobre Wonderpath EcoGames',
      sections: [
        {
          title: 'Acerca de la Edición Primera Infancia (3-6 años)',
          items: [
            {
              question: '¿Qué actividades incluye la Edición Primera Infancia?',
              answer: 'Ofrecemos múltiples actividades diseñadas para pequeños aprendices:\n\n🐰 "¡Ayuda a Beni el Conejito a Llegar a Casa!" - Un juego interactivo de saltos que introduce a los niños pequeños a la naturaleza a través del juego.\n\nEl Viaje de Beni enseña:\n• Movimientos básicos de animales y hábitats\n• Causa y efecto simple (tocar para saltar)\n• Conciencia espacial y dirección\n• Celebración del logro\n\n🎨 "EchoDraw" - ¡Una experiencia mágica de sonido a color donde los sonidos se convierten en arte visual!\n\nEchoDraw ofrece:\n• ¡No se necesita dibujar - solo usa tu voz!\n• Haz sonidos, observa cómo aparecen colores y formas\n• Perfecto para niños que prefieren jugar con sonidos en lugar de dibujar en pantalla\n• Todas las edades bienvenidas a explorar\n• Función de reproducción - ¡escucha tu creación cantar de vuelta!\n\nMás actividades próximamente enfocadas en:\n• Estaciones y patrones del clima\n• Colores y formas en la naturaleza\n• Conceptos simples de ecosistemas'
            },
            {
              question: '¿Qué es EchoDraw? A mi hijo/a no le gusta dibujar en pantallas.',
              answer: '¡EchoDraw es perfecto para niños que no disfrutan dibujar en pantalla! Es una actividad lúdica basada en sonido donde los niños usan su VOZ en lugar de herramientas de dibujo.\n\nCómo funciona:\n1. Hacer sonidos - tarareos, silbidos, aplausos, canto, o cualquier sonido vocal\n2. Observar cómo se transforman en formas y patrones coloridos\n3. Cada sonido crea diferentes colores y texturas visuales\n4. ¡Reproducir tu creación - te canta tus sonidos de vuelta!\n\nPor qué les encanta a los niños:\n• No se necesitan habilidades de dibujo\n• Natural e intuitivo - ¡solo haz sonidos!\n• Retroalimentación visual inmediata\n• Fomenta la exploración vocal y la creatividad\n• Excelente para niños que son más aprendices auditivos o kinestésicos\n• Todas las edades pueden explorar y experimentar\n\nEchoDraw es especialmente maravilloso para:\n• Niños que resisten las actividades tradicionales de pantalla\n• Niños que aman la música y el sonido\n• Explorar causa y efecto (sonido → color)\n• Juego sensorial y expresión creativa'
            },
            {
              question: '¿Es segura la Edición Primera Infancia para niños pequeños?',
              answer: '¡Sí! La Edición Primera Infancia está diseñada con seguridad y contenido apropiado para el desarrollo de niños de 3-6 años. Características incluyen:\n\n• No se requiere información personal\n• Experiencia sin anuncios\n• Visuales e interacciones apropiadas para la edad\n• Botones grandes y fáciles de tocar\n• Refuerzo positivo y aliento\n• Sin enlaces externos (excepto contenido para padres)\n• Diseño que prioriza la privacidad'
            },
            {
              question: '¿Cuánto tiempo debería jugar mi hijo/a?',
              answer: 'Los tiempos de actividad varían:\n• Actividad del Conejito: 2-5 minutos por sesión\n• EchoDraw: 3-10 minutos de exploración sonora\n\nPara niños pequeños de 3-6 años, recomendamos:\n• Sesiones de juego cortas (5-10 minutos)\n• Tiempo de juego supervisado\n• Tomar descansos entre actividades\n• Usar los juegos como parte de experiencias de juego variadas\n• Seguir el interés y nivel de energía de tu hijo/a\n\n¡Puedes repetir tantas veces como tu hijo/a disfrute! Algunos niños aman repetir EchoDraw para explorar diferentes sonidos y patrones.'
            }
          ]
        },
        {
          title: 'Acerca de Wonderpath Eco Game (8-12+ años)',
          items: [
            {
              question: '¿Qué es Wonderpath Eco Game?',
              answer: 'Wonderpath es un juego creativo multijugador de dibujo donde los niños aprenden sobre ecosistemas, ciencia climática y la regeneración de la naturaleza a través del juego, y desarrollan su pensamiento innovador y habilidades de comunicación visual. Piensa en Pictionary, pero con apoyo de artes y diseño y enfocado en conceptos ecológicos como polinización, redes alimentarias y el ciclo del agua.'
            },
            {
              question: '¿Para quién es?',
              answer: 'El juego está diseñado para edades 8-12+ (grados 3-8), pero funciona genial para familias, aulas, programas extracurriculares y cualquiera interesado en aprender sobre la naturaleza de forma divertida.'
            }
          ]
        }
      ],
      tagline: '"Donde la creatividad se encuentra con la educación climática."',
      mission: 'Wonderpath Eco Game • Apoyando los Objetivos de la ONU para Educación de Calidad, Acción Climática y Vida en la Tierra'
    }
  };

  const content = faqData[language];

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-sky to-primary-grass">
      {/* Back Button */}
      <div className="absolute top-8 left-8 z-50">
        <motion.button
          onClick={onBack}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-white/90 hover:bg-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2 text-gray-800 font-semibold text-2xl"
        >
          <span className="text-4xl">←</span>
          <span>{language === 'en' ? 'Back' : 'Volver'}</span>
        </motion.button>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-7xl font-bold text-white drop-shadow-lg mb-4">
            {content.title}
          </h1>
          <p className="text-4xl text-white/90 drop-shadow">
            {content.subtitle}
          </p>
        </motion.div>

        {/* FAQ Sections */}
        <div className="space-y-8">
          {content.sections.map((section, sectionIndex) => (
            <motion.div
              key={sectionIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: sectionIndex * 0.1 }}
              className="bg-white rounded-2xl shadow-xl overflow-hidden"
            >
              <div className="bg-gradient-to-r from-primary-orange to-primary-sunshine px-6 py-4">
                <h2 className="text-4xl font-bold text-white">
                  {section.title}
                </h2>
              </div>
              <div>
                {section.items.map((item, itemIndex) => (
                  <FAQItem
                    key={itemIndex}
                    question={item.question}
                    answer={item.answer}
                    isOpen={openIndex === sectionIndex * 100 + itemIndex}
                    onToggle={() => setOpenIndex(openIndex === sectionIndex * 100 + itemIndex ? null : sectionIndex * 100 + itemIndex)}
                  />
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-16 space-y-4"
        >
          <p className="text-5xl font-bold text-white drop-shadow-lg italic">
            {content.tagline}
          </p>
          <p className="text-3xl text-white/90 drop-shadow">
            {content.mission}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
